// Real, DB-backed adapter for every panel. NotImplementedError is kept for
// the composite's absence-degrade path (a 404/501 backend still falls back
// to mock rather than erroring the panel).

import axios from 'axios';
import { restHost } from '../../../apiConfig';
import { formatUrl, getCookie } from '../../../shared/utils';
import {
  ByDimensionResponse,
  ByUserResponse,
  CallDetail,
  CallStatsFilters,
  CallsPage,
  CallsQuery,
  ConnectivityResponse,
  FailuresResponse,
  IceType,
  MetricSample,
  PairSummary,
  QualityDimension,
  Scores,
  SummaryResponse,
} from '../callStatsTypes';
import { CallStatsDataSource } from './CallStatsDataSource';

// The hub POSTs per-5s WebRTC samples (JanusQualityMonitor) and the backend
// stores/returns them verbatim, so the wire shape differs from the chart's
// MetricSample. Map keys here rather than leak hub field names into the UI.
interface HubSample {
  t_s?: number;
  v_rtt_ms?: number;
  v_loss_pct?: number;
  a_loss_pct?: number;
  v_jitter_ms?: number;
  a_jitter_ms?: number;
  enc_kbps?: number;
  mos?: number;
}

const n = (v: number | undefined): number => (typeof v === 'number' ? v : 0);

function mapSample(s: HubSample, startedAtMs: number): MetricSample {
  return {
    // t_s is seconds from call start; synthesize the ISO stamp the chart wants.
    t: new Date(startedAtMs + n(s.t_s) * 1000).toISOString(),
    rttMs: n(s.v_rtt_ms),
    // single-line chart: show the worst of audio/video for loss + jitter.
    lossPct: Math.max(n(s.v_loss_pct), n(s.a_loss_pct)),
    jitterMs: Math.max(n(s.a_jitter_ms), n(s.v_jitter_ms)),
    bitrateKbps: n(s.enc_kbps),
    mos: n(s.mos),
  };
}

// Backend returns raw ICE candidate types (host|srflx|relay); the UI enum is
// host|srflx_stun|relay_turn. Normalize inbound.
const ICE_FROM_WIRE: Record<string, IceType> = {
  host: 'host',
  srflx: 'srflx_stun',
  srflx_stun: 'srflx_stun',
  relay: 'relay_turn',
  relay_turn: 'relay_turn',
};
function normalizeIce(raw: unknown): IceType | null {
  return typeof raw === 'string' && raw in ICE_FROM_WIRE
    ? ICE_FROM_WIRE[raw]
    : null;
}

export class NotImplementedError extends Error {
  constructor(method: string) {
    super(`call-stats: ${method} has no backend yet`);
    this.name = 'NotImplementedError';
  }
}

const BASE = 'api/v1/call-stats';

function qs(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.append(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

async function get<T>(path: string): Promise<T> {
  const res = await axios.get<T>(formatUrl(restHost, path), {
    headers: { Authorization: `Bearer ${getCookie('access_token')}` },
  });
  return res.data;
}

function filterParams(f: CallStatsFilters): Record<string, string | undefined> {
  return {
    window: f.window,
    from: f.window === 'custom' ? f.from : undefined,
    to: f.window === 'custom' ? f.to : undefined,
    userId: f.userId,
    peerUserId: f.peerUserId,
  };
}

export class RestCallStatsDataSource implements CallStatsDataSource {
  getSummary(filters: CallStatsFilters): Promise<SummaryResponse> {
    return get<SummaryResponse>(`${BASE}/summary${qs(filterParams(filters))}`);
  }

  getByUser(filters: CallStatsFilters, limit = 10): Promise<ByUserResponse> {
    return get<ByUserResponse>(
      `${BASE}/by-user${qs({ ...filterParams(filters), limit })}`,
    );
  }

  getPair(filters: CallStatsFilters): Promise<PairSummary> {
    return get<PairSummary>(
      `${BASE}/pair${qs({
        userA: filters.userId,
        userB: filters.peerUserId,
        window: filters.window,
        from: filters.window === 'custom' ? filters.from : undefined,
        to: filters.window === 'custom' ? filters.to : undefined,
      })}`,
    );
  }

  getFailures(filters: CallStatsFilters): Promise<FailuresResponse> {
    return get<FailuresResponse>(
      `${BASE}/failures${qs(filterParams(filters))}`,
    );
  }

  getCalls(query: CallsQuery): Promise<CallsPage> {
    return get<CallsPage>(
      `${BASE}/calls${qs({
        ...filterParams(query),
        type: query.type,
        status: query.status,
        page: query.page,
        pageSize: query.pageSize,
      })}`,
    );
  }

  getScores(filters: CallStatsFilters): Promise<Scores> {
    return get<Scores>(`${BASE}/quality/scores${qs(filterParams(filters))}`);
  }

  getByDimension(
    filters: CallStatsFilters,
    dimension: QualityDimension,
  ): Promise<ByDimensionResponse> {
    // by-dimension is not user-scoped server-side — drop userId so we don't send a silently-ignored filter (per-user scoping tracked in OI-1602).
    const { userId: _userId, ...params } = filterParams(filters);
    return get<ByDimensionResponse>(
      `${BASE}/quality/by-dimension${qs({ ...params, dimension })}`,
    );
  }

  getConnectivity(filters: CallStatsFilters): Promise<ConnectivityResponse> {
    // connectivity is not user-scoped server-side — see getByDimension.
    const { userId: _userId, ...params } = filterParams(filters);
    return get<ConnectivityResponse>(`${BASE}/connectivity${qs(params)}`);
  }

  async getCallDetail(callId: string): Promise<CallDetail> {
    const raw = await get<
      Omit<CallDetail, 'timeseries' | 'iceType'> & {
        timeseries?: HubSample[];
        iceType?: unknown;
      }
    >(`${BASE}/calls/${encodeURIComponent(callId)}/quality`);
    const startedAtMs = new Date(raw.startedAt).getTime();
    return {
      ...raw,
      iceType: normalizeIce(raw.iceType),
      timeseries: (raw.timeseries ?? []).map((s) => mapSample(s, startedAtMs)),
    };
  }
}
